import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { DriverCard } from './components/DriverCard'
import { RouteDetailView } from './components/RouteDetailView'
import { DriverFilters } from './components/DriverFilters'
import type { DriverSchedule } from './types'
import { useDriverTracking } from './hooks/useDriverTracking'
import { DriverMap } from './components/DriverMap'
import { useDriverFilters } from './hooks/useDriverFilters'
import { getInitialDriverTrackingBounds, getInitialDriverViewState, passesSelectedFilters } from './util'
import { Empty, Spin } from 'antd'
import { useMapboxMap } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { useDriverTrackingState, actions } from './stores'
import dayjs from 'dayjs'

const MAX_RESULTS_W_BREADCRUMBS = 15 // Technically any EP resp w less than 15 results will include breadcrumbs.
// Not using it for now, since we always want to use singleDriverResult since it refreshes every 20 seconds

export function DriverTracking() {
  const { selectedDriverId } = useDriverTrackingState()
  const [selectedDriverNoBreadcrumbs, setSelectedDriverNoBreadcrumbs] = useState<DriverSchedule | null | undefined>(
    null
  ) // Used to display initial driver info while we fetch the single driver with breadcrumbs
  const mapboxMapHook = useMapboxMap('driver-tracking')
  const { mapRef, flyToLocation, getMapLayerIds, toggleLayerVisibility } = mapboxMapHook
  const mapZoomRef = useRef<boolean>(false)

  const { pointsOfInterestQuery, allDriversQuery, driverSearchQuery, singleDriverQuery, setActiveQueryFilters } =
    useDriverTracking()

  const {
    data: { data: allDriversResult } = { data: [] },
  } = allDriversQuery
  const {
    data: { data: driverSearchResult } = { data: [] },
  } = driverSearchQuery
  const {
    data: { data: singleDriverResult } = { data: [] },
  } = singleDriverQuery
  const {
    data: { data: pointsOfInterest } = { data: [] },
  } = pointsOfInterestQuery

  const driverFiltersHook = useDriverFilters(allDriversResult, setActiveQueryFilters)
  const { selectedFilters, handleFilterChange } = driverFiltersHook

  /** Handle URL search params (this is what enables us to link from another page) */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const orderNumber = urlParams.get('order_number')

    setActiveQueryFilters((prev) => ({
      ...prev,
      ...(orderNumber ? { order_numbers: [Number(orderNumber)] } : { shift_date: dayjs().toISOString() }),
    }))
    return () => {
      handleFilterChange({ search_term: null })
      actions.setSelectedDriverId(null)
    }
  }, [])

  useEffect(() => {
    if (!mapRef?.current || mapZoomRef.current || selectedDriverId) return
    const bounds = getInitialDriverTrackingBounds(pointsOfInterest)
    if (bounds) {
      mapRef?.current.fitBounds(bounds, { padding: { bottom: 200 } })
      mapZoomRef.current = true
    }
  }, [pointsOfInterest, mapRef?.current])

  /** If only one search result is given, automatically select that driver and zoom in on their location */
  useEffect(() => {
    if (driverSearchResult.length === 1) {
      const singleDriver = driverSearchResult[0]
      selectDriver(singleDriver)
    }
  }, [driverSearchResult])

  const driverSchedules = useMemo(() => {
    return allDriversResult
      .filter((driver) => passesSelectedFilters(driver, selectedFilters))
      .sort((a, b) => a.driver_name.localeCompare(b.driver_name))
  }, [selectedFilters, allDriversResult])

  const driverList = useMemo(
    () => (selectedFilters.type === 'filter' || driverSearchResult.length <= 1 ? driverSchedules : driverSearchResult),
    [selectedFilters, driverSearchResult, driverSchedules]
  )
  const initialViewState = useMemo(() => getInitialDriverViewState(driverSchedules), [driverSchedules])

  const flyToDriver = useCallback(
    (driver: DriverSchedule | null | undefined) => {
      if (driver) {
        mapZoomRef.current = true
        const live = driver.last_update
        if (live?.lon && live.lat) {
          flyToLocation([live.lon, live.lat])
        }
      } else {
        flyToLocation([initialViewState.longitude, initialViewState.latitude], initialViewState.zoom)
      }
    },
    [flyToLocation, initialViewState]
  )

  function selectDriver(driver: DriverSchedule) {
    actions.setSelectedDriverId(driver.driver_id)
    setSelectedDriverNoBreadcrumbs(driver)
    actions.initializeOrdersOnRoute(driver)

    flyToDriver(driver)
    setActiveQueryFilters((val) => ({ ...val, driver_ids: [driver.driver_id] }))
  }

  function handleDriverSelect(driverId: string) {
    const driver = driverList.find((driver) => driver.driver_id === driverId)
    if (driver) {
      selectDriver(driver)
    }
  }

  function handleDriverClose() {
    actions.setSelectedDriverId(null)
    actions.clearOrdersOnRoute()
    if (driverFiltersHook.selectedFilters.type === 'search') {
      setActiveQueryFilters((prev) => ({
        ...prev,
        driver_names: [],
        order_numbers: [],
      }))
    }
    setSelectedDriverNoBreadcrumbs(null)
    handleFilterChange({ search_term: null })

    // Clear URL search params
    window.history.replaceState({}, '', window.location.pathname)
    // Reset map view
    const bounds = getInitialDriverTrackingBounds(driverSchedules)
    if (bounds) {
      mapRef?.current?.fitBounds(bounds, { padding: 50 })
    }
  }

  return (
    <Vertical style={{ position: 'relative' }}>
      <Horizontal height={'100%'}>
        <Vertical style={{ width: '25%', backgroundColor: 'var(--bg-1)', minWidth: '430px' }} className='p-3'>
          <DriverFilters driverFiltersHook={driverFiltersHook} />
          {selectedDriverId === null ? (
            <Vertical style={{ overflowY: 'auto' }} className='mt-2'>
              {driverList.length === 0 ? (
                <EmptyDriverList isLoading={allDriversQuery.isLoading} />
              ) : (
                driverList.map((liveDriver) => (
                  <DriverCard
                    key={liveDriver.driver_schedule_id}
                    driverSchedule={liveDriver}
                    handleDriverSelect={handleDriverSelect}
                  />
                ))
              )}
            </Vertical>
          ) : (
            <RouteDetailView
              driverScheduleNoBreadcrumbs={selectedDriverNoBreadcrumbs}
              driverScheduleWithBreadcrumbs={singleDriverResult?.[0]}
              isLoading={singleDriverQuery.isLoading}
              handleClose={handleDriverClose}
              flyToLocation={flyToLocation}
              getMapLayerIds={getMapLayerIds}
              toggleLayerVisibility={toggleLayerVisibility}
            />
          )}
        </Vertical>
        <DriverMap
          mapboxMapHook={mapboxMapHook}
          allDriverTracking={driverList}
          pointsOfInterest={pointsOfInterest}
          driverSchedule={singleDriverResult?.[0]}
          selectedDriverId={selectedDriverId}
          handleDriverSelect={handleDriverSelect}
          initialViewState={initialViewState}
        />
      </Horizontal>
    </Vertical>
  )
}

function EmptyDriverList({ isLoading }: { isLoading?: boolean } = {}) {
  return (
    <Vertical justifyContent='center' alignItems='center'>
      {isLoading ? <Spin /> : <Empty description={'No drivers found.'} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Vertical>
  )
}
